import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

import { jobService } from "../services/jobService";

// The job title, as a way of getting to another job.
//
// ─── WHY THE TITLE AND NOT A SEPARATE CONTROL ───────────────────────────────
//
// Moving between two jobs meant: breadcrumb to Jobs, find the card among
// however many there are, click in. Three navigations and a visual search to do
// the thing recruiters do most — the same review on the next role.
//
// The title is the obvious place for it: it already says which job you are
// looking at, which is exactly the question a switcher answers. Adding a
// separate dropdown beside it would put the job's identity in two places and
// leave the reader to work out which one is the control.
//
// ─── WHAT IT SHOWS, AND WHY ─────────────────────────────────────────────────
//
// Every job for the store, ordered as the dashboard orders them, with the
// candidate count and the status. The count is the thing that makes the list
// scannable — "ERPNext Developer 7" is recognisable in a way that a list of
// similar titles is not — and the status matters because a closed job is a
// legitimate place to go (to read what happened) but not somewhere you want to
// land by accident.
//
// A filter box appears once there are enough jobs for scanning to stop working.
// Below that it is noise, and it steals the keyboard from the arrow keys that
// already work.

const C = {
  ink: "#101828",
  body: "#344054",
  faint: "#667085",
  hair: "#e4e7ec",
  wash: "#f9fafb",
  accent: "#037e8f",
};
const FONT = "'Plus Jakarta Sans', 'Inter', sans-serif";

// Above this many, the eye stops being able to find a title in the list.
const FILTER_FROM = 7;

const STATUS = {
  ACTIVE: { label: "Open", fg: "#067647", bg: "#ecfdf3", line: "#abefc6" },
  DRAFT: { label: "Draft", fg: "#b54708", bg: "#fffaeb", line: "#fedf89" },
  CLOSED: { label: "Closed", fg: "#b42318", bg: "#fef3f2", line: "#fecdca" },
};

export default function JobSwitcher({ currentJob, storeId, onPick }) {
  const [anchor, setAnchor] = useState(null);
  const [jobs, setJobs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const searchRef = useRef(null);

  const open = Boolean(anchor);

  // Fetched when the menu is opened, not on mount.
  //
  // Every visit to a job page would otherwise pay for a list of every other
  // job — a request whose result is only ever used by somebody who is leaving.
  // Re-fetched on each open rather than cached, because the interesting case is
  // "I just created a job in another tab" and a stale list is the one thing a
  // switcher must not be.
  useEffect(() => {
    if (!open) return;
    if (storeId === null || storeId === undefined) return;
    let alive = true;
    setLoading(true);
    setError("");
    jobService
      .getAllJobs(storeId)
      .then((res) => {
        if (!alive) return;
        const body = res?.data;
        if (!body?.success) throw new Error(body?.statusMessage || "Could not load jobs.");
        setJobs(body.data || []);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.response?.data?.statusMessage || e?.message || "Could not load jobs.");
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [open, storeId]);

  // Whether the filter box is on screen. Read twice — by the render and by the
  // menu's focus decision — so the two cannot disagree about whether there is
  // an input to focus.
  const showFilter = (jobs?.length || 0) >= FILTER_FROM;

  const filtered = useMemo(() => {
    const list = jobs || [];
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((j) => String(j.title || "").toLowerCase().includes(needle));
  }, [jobs, q]);

  const close = () => {
    setAnchor(null);
    // Cleared on close so the next open starts from the whole list. A filter
    // left in place would make the switcher look like the store has one job.
    setQ("");
  };

  const pick = (job) => {
    close();
    if (!job || Number(job.id) === Number(currentJob?.id)) return;
    onPick?.(job);
  };

  return (
    <>
      <Box
        onClick={(e) => setAnchor(e.currentTarget)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setAnchor(e.currentTarget);
          }
        }}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          borderRadius: 1.5,
          mx: -0.75,
          px: 0.75,
          maxWidth: "100%",
          minWidth: 0,
          transition: "background .15s",
          "&:hover": { bgcolor: C.hair },
          "&:hover .jd-switch-caret": { color: C.accent },
        }}
        title="Switch to another job"
      >
        {/* Keeps the page's own title styling — this is still the heading, it
            just happens to be operable. */}
        <Typography className="jd-title" sx={{ minWidth: 0 }} noWrap>
          {currentJob?.title || "Job"}
        </Typography>
        <ExpandMoreRoundedIcon
          className="jd-switch-caret"
          sx={{ fontSize: 22, color: C.faint, flexShrink: 0, transition: "color .15s" }}
        />
      </Box>

      <Menu
        anchorEl={anchor}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        // Focus goes to the filter box when there is one, so typing filters
        // instead of being swallowed by MUI's type-to-select on the list items.
        //
        // When there is no filter box, the menu keeps its own autofocus —
        // disabling it unconditionally left nothing focused on a short list, so
        // the arrow keys did nothing until somebody clicked a row first.
        autoFocus={!showFilter}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              minWidth: 340,
              maxWidth: 460,
              borderRadius: 2.5,
              boxShadow: "0 12px 34px rgba(16,24,40,.14)",
              border: `1px solid ${C.hair}`,
            },
          },
        }}
      >
        <Typography
          sx={{
            fontFamily: FONT,
            fontSize: 10.5,
            fontWeight: 800,
            letterSpacing: 0.7,
            color: C.faint,
            px: 2,
            pt: 1.25,
            pb: 0.75,
          }}
        >
          SWITCH JOB
        </Typography>

        {showFilter && (
          <Box sx={{ px: 1.5, pb: 1 }}>
            <TextField
              inputRef={searchRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              // Stops MUI's menu keyboard handling from stealing the keystrokes
              // and jumping to an item beginning with that letter.
              onKeyDown={(e) => e.stopPropagation()}
              placeholder="Filter jobs…"
              size="small"
              fullWidth
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ fontSize: 17, color: C.faint }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 },
                "& .MuiInputBase-input": { fontFamily: FONT },
              }}
            />
          </Box>
        )}

        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2, py: 1.5 }}>
            <CircularProgress size={14} sx={{ color: C.faint }} />
            <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: C.faint }}>
              Loading jobs…
            </Typography>
          </Box>
        )}

        {!loading && error && (
          <Typography
            sx={{ fontFamily: FONT, fontSize: 12.5, color: "#b42318", px: 2, py: 1.5 }}
          >
            {error}
          </Typography>
        )}

        {!loading && !error && filtered.length === 0 && (
          <Typography
            sx={{ fontFamily: FONT, fontSize: 12.5, color: C.faint, px: 2, py: 1.5 }}
          >
            {q.trim() ? `No job matches “${q.trim()}”.` : "No other jobs yet."}
          </Typography>
        )}

        <Box sx={{ maxHeight: 340, overflowY: "auto" }}>
          {!loading &&
            !error &&
            filtered.map((j) => {
              const here = Number(j.id) === Number(currentJob?.id);
              const st = STATUS[j.status] || STATUS.ACTIVE;
              return (
                <MenuItem
                  key={j.id}
                  onClick={() => pick(j)}
                  // Selected rather than disabled: the job you are on should be
                  // findable in the list — it is how you confirm where you are —
                  // and a disabled row reads as "broken" rather than "current".
                  selected={here}
                  sx={{
                    px: 2,
                    py: 1,
                    gap: 1,
                    alignItems: "center",
                    "&.Mui-selected": { bgcolor: C.wash },
                    "&.Mui-selected:hover": { bgcolor: C.wash },
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: FONT,
                        fontSize: 13.5,
                        fontWeight: here ? 750 : 600,
                        color: C.ink,
                      }}
                      noWrap
                    >
                      {j.title}
                    </Typography>
                    <Typography
                      sx={{ fontFamily: FONT, fontSize: 11.5, color: C.faint, mt: 0.15 }}
                    >
                      {/* The count is what makes similar titles tellable apart. */}
                      {j.candidate_count === 0
                        ? "No candidates yet"
                        : `${j.candidate_count} candidate${j.candidate_count === 1 ? "" : "s"}`}
                    </Typography>
                  </Box>

                  <Chip
                    size="small"
                    label={st.label}
                    sx={{
                      height: 19,
                      fontFamily: FONT,
                      fontSize: 10.5,
                      fontWeight: 700,
                      color: st.fg,
                      bgcolor: st.bg,
                      border: `1px solid ${st.line}`,
                      flexShrink: 0,
                    }}
                  />

                  {here && (
                    <CheckRoundedIcon
                      sx={{ fontSize: 17, color: C.accent, flexShrink: 0 }}
                    />
                  )}
                </MenuItem>
              );
            })}
        </Box>

        {!loading && !error && (jobs?.length || 0) > 0 && (
          <>
            <Divider sx={{ my: 0.5, borderColor: C.hair }} />
            <MenuItem
              onClick={() => {
                close();
                onPick?.(null); // null means "the jobs list"
              }}
              sx={{ px: 2, py: 1 }}
            >
              <Typography
                sx={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 650, color: C.body }}
              >
                See all jobs
              </Typography>
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
}
